import {
  resolveXlsxFileName,
  triggerBlobDownload,
} from '../../utils/http/responseHelpers';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { Statistic } from '../../models/RoboCall/Statistic';
import { CallStatistic } from '../../models/RoboCall/statisticModels/CallStatistic';
import { Status } from '../../models/common/Status';
import { RootState } from '../store';
import { apiClientWithApi } from '../../utils/http';

export interface GetStatisticRequest {
  begin: string;
  end: string;
  campaignIds: number[];
}

export interface StatisticsState {
  statistic: Statistic;
  status: Status;
}

const initialState: StatisticsState = {
  statistic: {
    callStatistics: {
      campaignTasks: 0,
      tasks: 0,
      calls: 0,
      answeredCalls: 0,
      notAnswered: 0,
      notProcessed: 0,
    } as CallStatistic,
  } as Statistic,
  status: {
    responseLoading: true,
    responseError: null,
    requestSuccess: null,
    requestError: null,
  },
};

export const getDefaultStatistics = createAsyncThunk(
  'statistic/defaultStatistics',
  async (_, { rejectWithValue }) => {
    try {
      var qs = require('qs');
      const response = await apiClientWithApi.get<Statistic>(
        'statistic/defaultStatistics',
        {
          paramsSerializer: function (params) {
            return qs.stringify(params, { arrayFormat: 'repeat' });
          },
        },
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.message);
      } else
        return rejectWithValue(
          'Не удалось получить статистику! Серверная ошибка!',
        );
    }
  },
);

export const getStatisticsWithParams = createAsyncThunk(
  'statistic/getStatistic',
  async (request: GetStatisticRequest, { rejectWithValue }) => {
    try {
      var qs = require('qs');
      const response = await apiClientWithApi.get<Statistic>('statistic', {
        params: {
          begin: request.begin,
          end: request.end,
          campaignIds: request.campaignIds,
        },
        paramsSerializer: function (params) {
          return qs.stringify(params, { arrayFormat: 'repeat' });
        },
      });

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.message);
      } else
        return rejectWithValue(
          'Не удалось получить статистику! Серверная ошибка!',
        );
    }
  },
);

export const getExcelStatistics = createAsyncThunk(
  'Statistic/excelStatistics',
  async (request: GetStatisticRequest, { rejectWithValue }) => {
    try {
      var qs = require('qs');
      const response = await apiClientWithApi.get('statistic/excelStatistics', {
        params: {
          begin: request.begin,
          end: request.end,
          campaignIds: request.campaignIds,
        },
        responseType: 'blob',
        paramsSerializer: function (params) {
          return qs.stringify(params, { arrayFormat: 'repeat' });
        },
      });
      const fileName = resolveXlsxFileName(
        response.headers['content-disposition'],
        `statistics_${request.begin}_${request.end}.xlsx`,
      );
      triggerBlobDownload(new Blob([response.data]), fileName);
      return fileName;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.message);
      } else
        return rejectWithValue(
          'Не удалось загрузить статистику! Серверная ошибка!',
        );
    }
  },
);

export const statisticsSlice = createSlice({
  name: 'statistics',
  initialState,
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(getDefaultStatistics.pending, (state) => {
        state.status.responseLoading = true;
        state.status.responseError = null;
      })
      .addCase(getDefaultStatistics.fulfilled, (state, action) => {
        state.status.responseLoading = false;
        state.status.responseError = null;
        state.statistic = action.payload as Statistic;
      })
      .addCase(getStatisticsWithParams.pending, (state: StatisticsState) => {
        state.status.responseLoading = true;
        state.status.responseError = null;
      })
      .addCase(getStatisticsWithParams.fulfilled, (state, action) => {
        state.status.responseLoading = false;
        state.status.responseError = null;
        state.statistic = action.payload as Statistic;
      })
      .addCase(getExcelStatistics.pending, (state: StatisticsState) => {
        state.status.responseLoading = true;
        state.status.responseError = null;
      })
      .addCase(getExcelStatistics.fulfilled, (state) => {
        state.status.responseLoading = false;
        state.status.responseError = null;
      })
      .addCase(getExcelStatistics.rejected, (state) => {
        state.status.responseLoading = false;
        state.status.responseError = 'Не удалось загрузить статистику';
      })
      .addCase(getStatisticsWithParams.rejected, (state: StatisticsState) => {
        state.status.responseLoading = false;
        state.status.responseError = 'Не удалось загрузить статистику';
      });
  },
});

export default statisticsSlice.reducer;

export const selectStatistics = (state: RootState) =>
  state.statistics.statistic;

export const selectStatisticsError = (state: RootState) =>
  state.statistics.status.responseError;

export const selectStatisticsLoading = (state: RootState) =>
  state.statistics.status.responseLoading;

export const statisticsStatus = (state: RootState) => state.statistics.status;
